import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { submitContactForm, companyInfo } from '../mockData';
import { Mail, Phone, MapPin, ChevronRight, Send, Download } from 'lucide-react';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    projectLocation: '',
    industry: '',
    timeline: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const result = await submitContactForm(formData);
      setSubmitMessage(result.message);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', company: '', service: '', projectLocation: '', industry: '', timeline: '', budget: '', message: '' });
    } catch (error) {
      setSubmitMessage(error.message || 'An error occurred. Please try again.');
      setSubmitSuccess(false);
    }
    setIsSubmitting(false);
  };

  const offices = [
    {
      city: 'Dubai, UAE',
      company: 'HLA Technical Services LLC',
      phone: companyInfo.phone.uae,
      address: '411/46, Pinnacle Building, Al Barsha First, Sheikh Zayed Road, Dubai, UAE',
    },
    {
      city: 'Ajman, UAE',
      company: 'Hexalogix Automation Power Control Switchgears Manufacturing LLC',
      phone: companyInfo.phone.ajman,
      address: 'Shed No: 1, Al Bahia, Ajman, UAE',
    },
    {
      city: 'Kochi, India',
      company: 'Hexalogix Automation',
      phone: companyInfo.phone.india,
      address: 'Building No 21/246-(1), 3rd Floor, Grande Tower, NH By-pass, Aluva - 683101, Kerala, India',
    },
  ];

  const inputClass = "w-full bg-[#F0F4F9] border border-gray-200 focus:border-[#0057B8] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0057B8]/20 text-gray-900 placeholder-gray-400 text-base px-4 py-3.5 transition-all duration-200";

  return (
    <>
      <SEOHead
        title="Contact Hexalogix Automation | UAE & India Offices"
        description="Get in touch with Hexalogix Automation for industrial automation projects across UAE, Africa, and India. Phone: +971 55 916 2533. Email: info@hexalogixautomation.com."
        keywords="contact hexalogix, automation company UAE, automation company Africa, control panel manufacturer contact, PLC programming services UAE"
      />

      <div className="min-h-screen">

        {/* ═══════════════════ PAGE HERO ═══════════════════ */}
        <section className="relative pt-16 md:pt-[106px] overflow-hidden bg-[#0A0F1E]">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1920&h=400&fit=crop&q=60"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-300">Contact</span>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
              <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Reach Out</span>
              <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4">
              Let's Talk
            </h1>
            <p className="text-gray-300 text-lg max-w-xl">
              Ready to transform your operations? Our engineers are here to help you build the right automation solution.
            </p>
          </div>
        </section>

        {/* ═══════════════════ CONTACT BODY ═══════════════════ */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

              {/* ── Contact Form (3/5) ── */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Send a Message</span>
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-[#0A0F1E] mb-8">
                  Tell Us About Your Project
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+971 55 000 0000"
                        value={formData.phone}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Company</label>
                      <input
                        type="text"
                        name="company"
                        placeholder="Your Company Name"
                        value={formData.company}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      placeholder="Tell us briefly about your requirement or question..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className={inputClass + ' resize-none'}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 bg-[#0057B8] hover:bg-[#003D82] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-[#0057B8]/30 group"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>

                  {submitMessage && (
                    <div className={`flex items-start gap-3 p-4 border text-base ${submitSuccess ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
              </div>

              {/* ── Contact Info (2/5) ── */}
              <div className="lg:col-span-2 space-y-8">
                {/* Email */}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                    <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Contact Details</span>
                    <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  </div>
                  <div className="space-y-3 mt-4">
                    <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 group">
                      <div className="w-9 h-9 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0057B8] transition-colors">
                        <Mail className="w-4 h-4 text-[#0057B8] group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-base text-gray-600 group-hover:text-[#0057B8] transition-colors break-all">
                        {companyInfo.email}
                      </span>
                    </a>
                    <a href={`mailto:${companyInfo.emailSales}`} className="flex items-center gap-3 group">
                      <div className="w-9 h-9 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0057B8] transition-colors">
                        <Mail className="w-4 h-4 text-[#0057B8] group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-base text-gray-600 group-hover:text-[#0057B8] transition-colors break-all">
                        {companyInfo.emailSales}
                      </span>
                    </a>
                  </div>
                </div>

                {/* Offices */}
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                    <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Our Offices</span>
                    <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  </div>
                  <div className="space-y-5">
                    {offices.map((office, i) => (
                      <div key={i} className="border border-gray-100 hover:border-[#0057B8]/30 bg-[#F8FAFD] hover:bg-[#EBF4FF]/50 p-5 transition-all duration-200">
                        <h4 className="font-black text-[#0A0F1E] text-base mb-3">{office.city}</h4>
                        <p className="text-sm font-semibold text-[#0057B8] mb-2 leading-relaxed">{office.company}</p>
                        <a href={`tel:${office.phone}`} className="flex items-center gap-2.5 text-base text-gray-600 hover:text-[#0057B8] transition-colors mb-2">
                          <Phone className="w-3.5 h-3.5 text-[#0057B8] flex-shrink-0" />
                          {office.phone}
                        </a>
                        <div className="flex items-start gap-2.5 text-sm text-gray-500">
                          <MapPin className="w-3.5 h-3.5 text-[#0057B8] flex-shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{office.address}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ═══════════════════ CTA STRIP ═══════════════════ */}
        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="absolute right-16 top-0 bottom-0 w-16 bg-white/5 -skew-x-12" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-10">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Need an Urgent Response?</h2>
              <p className="text-blue-200 max-w-lg">Call our UAE office directly for immediate assistance.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <a href={`tel:${companyInfo.phone.uae}`}>
                <button className="inline-flex items-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 group">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone.uae}
                </button>
              </a>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 text-base tracking-wide transition-all duration-200 group"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Our Brochure
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

